/**
 * Wave K (decision 0065, spec `docs/design/wave-k-knowledge-spec.md` §4.1): search over a project's records, history
 * lookup over team chat and run transcripts, the decided-cards projection and the usage log.
 *
 * Two indexes per project, one per question: **records** (`search`: items, goals, KPIs, the brief, decisions, comments,
 * outputs) and **history** (`look_back`: team chat messages and worker/validator run transcripts at message level,
 * never tool output). History is never searched on every turn and never injected into a pack.
 *
 * No I/O. This module must not import `./mcp` (mcp.ts spreads {@link KnowledgeToolInputs} into `GraphToolInputs`).
 */
import { Schema } from "effect"
import { EntityRef } from "./common"
import { Millis, NonNegInt, PosInt, ProposalId, ShortRef } from "./ids"

// #region kinds
/** Kinds of the records index. `memory` joined with wave C (decision 0071); `note` joins with K3. */
export const SearchKind = Schema.Literals([
  "item",
  "goal",
  "kpi",
  "brief",
  "decision",
  "comment",
  "output",
  "memory",
])
export type SearchKind = typeof SearchKind.Type
/** The record kinds in the order the results page and `search` group them. */
export const SEARCH_KIND_ORDER: ReadonlyArray<SearchKind> = [
  "item",
  "decision",
  "memory",
  "goal",
  "kpi",
  "output",
  "brief",
  "comment",
]

/** What `look_back` searches. */
export const HistoryKind = Schema.Literals(["chat", "runs"])
export type HistoryKind = typeof HistoryKind.Type

/**
 * Chat visibility (decision 0065). Only `team` is ever indexed; wave E adds `only_me` chats, which never are (the
 * Project DO's `history_docs` table refuses anything but `team` with a check constraint).
 */
export const ChatVisibility = Schema.Literals(["team", "only_me"])
export type ChatVisibility = typeof ChatVisibility.Type

/** How a hit shows: settled first, greyed when replaced / rejected / cancelled / expired. */
export const HitState = Schema.Literals([
  "settled",
  "open",
  "draft",
  "proposed",
  "replaced",
  "rejected",
  "cancelled",
  "expired",
  "resolved",
])
export type HitState = typeof HitState.Type
// #endregion kinds

// #region hits
/** One record hit. */
export const SearchHit = Schema.Struct({
  /**
   * The doc key: "item:itm_…", "goal:…", "kpi:…", "comment:cmt_…", "decision:prop_…", "output:art_…", "memory:mem_…"
   * (wave C), "brief", "brief@<seq>".
   */
  key: Schema.String,
  kind: SearchKind,
  /** DIC-12, G-2, K-3, P-3, O-14, M-7; null for comments and the brief. */
  ref: Schema.NullOr(Schema.String),
  title: Schema.String,
  /** ≤ 240 chars, matches wrapped in « ». "" when withheld (a tainted output, for agents). */
  snippet: Schema.String,
  /** What a comment, output or decision comment sits on. */
  on: Schema.NullOr(Schema.Struct({ ref: Schema.NullOr(Schema.String), title: Schema.String })),
  settled: Schema.Boolean,
  state: HitState,
  /** The record's last change. */
  at: Millis,
  /** Outside text: shown and passed to agents as quoted data. */
  tainted: Schema.Boolean,
  /** App path below /w/<ws>/p/<project>: "/i/DIC-12", "?view=needs&card=prop_…", … */
  path: Schema.String,
  /** What `read` takes: the ref, or "comment:<id>", "brief", "brief@<seq>", "O-14@v1" (an older version). */
  anchor: Schema.String,
  /**
   * Outputs (one `O-n` per version chain, decision 0065): older versions of this chain that matched too, folded under
   * the head ("also in v1"), oldest first. Absent when none did.
   */
  also: Schema.optionalKey(Schema.Array(Schema.Struct({ anchor: Schema.String, version: PosInt }))),
})
export type SearchHit = typeof SearchHit.Type

/** Who wrote a history message. */
export const HistoryWho = Schema.Struct({
  _tag: Schema.Literals(["user", "agent", "worker", "validator"]),
  id: Schema.String,
  label: Schema.String,
})
export type HistoryWho = typeof HistoryWho.Type

/** One history hit: a chat message or a run transcript message (or a run's final report). */
export const HistoryHit = Schema.Struct({
  /** "chat:<chatId>:<messageId>" | "run:<runId>:<frameSeq>" | "run:<runId>:report". */
  anchor: Schema.String,
  kind: Schema.Literals(["chat", "run"]),
  at: Millis,
  role: Schema.Literals(["person", "agent", "assistant", "tools", "steer", "report"]),
  who: HistoryWho,
  /** "mcp:claude-code" for a message a person sent from their CLI. */
  via: Schema.NullOr(Schema.String),
  /** ≤ 300 chars, « » marks; "" when withheld (a tainted run or a tainted Lea reply, for agents). */
  snippet: Schema.String,
  withheld: Schema.Boolean,
  /** Run hits: the run and its item (`itemTitle` is the current title, read at query time). */
  run: Schema.NullOr(
    Schema.Struct({
      ref: Schema.String,
      kind: Schema.Literals(["worker", "validator"]),
      itemRef: Schema.NullOr(Schema.String),
      itemTitle: Schema.NullOr(Schema.String),
    }),
  ),
  /** "?view=chat&agent=<chatId>&msg=<id>" | "?run=<id>&frame=<seq>". */
  path: Schema.String,
  tainted: Schema.Boolean,
  /** A chat message backfilled without a stored date: `at` is a neighbour's date or the backfill time. Absent = false. */
  dateUnknown: Schema.optionalKey(Schema.Boolean),
})
export type HistoryHit = typeof HistoryHit.Type

/** A chat hit in `search` answers: a history hit of kind `chat`. */
export const ChatHit = HistoryHit
export type ChatHit = HistoryHit
// #endregion hits

// #region results
export const SearchMatched = Schema.Literals(["all", "any", "none"])
export type SearchMatched = typeof SearchMatched.Type

export const SearchGroup = Schema.Struct({
  kind: SearchKind,
  hits: Schema.Array(SearchHit),
  /** Hits of this kind not shown (beyond `limit`). */
  more: NonNegInt,
})
export type SearchGroup = typeof SearchGroup.Type

export const SearchResult = Schema.Struct({
  /** The usage-log row id, for `SearchOpened`. */
  searchId: Schema.String,
  query: Schema.String,
  /** All words matched, any word (the fallback), or nothing. */
  matched: SearchMatched,
  /** An exact ref or an exact title. */
  jump: Schema.NullOr(SearchHit),
  groups: Schema.Array(SearchGroup),
  /** At most three team chat hits; always empty for workers and validators. */
  chat: Schema.Struct({ hits: Schema.Array(HistoryHit), more: NonNegInt }),
  /** The index is dirty or being rebuilt: hits are what exists (spec §5.7). */
  building: Schema.Boolean,
  /** Decision 0146: hits found by meaning too (the semantic channel ran and contributed). Absent = keyword-only. */
  meaning: Schema.optionalKey(Schema.Boolean),
})
export type SearchResult = typeof SearchResult.Type

export const LookBackResult = Schema.Struct({
  searchId: Schema.String,
  query: Schema.String,
  window: Schema.Struct({ since: Millis, until: Millis }),
  hits: Schema.Array(HistoryHit),
  more: Schema.Boolean,
  building: Schema.Boolean,
  /** Decision 0146: hits found by meaning too. Absent = keyword-only. */
  meaning: Schema.optionalKey(Schema.Boolean),
})
export type LookBackResult = typeof LookBackResult.Type
// #endregion results

// #region tool-inputs
/** A date bound: epoch ms or YYYY-MM-DD (UTC midnight). */
export const DateBound = Schema.Union([Millis, Schema.String.check(Schema.isPattern(/^\d{4}-\d{2}-\d{2}$/))])
export type DateBound = typeof DateBound.Type

/** A search query: 1–200 characters. */
export const KnowledgeQuery = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200))

/** `ToolRef` of mcp.ts, repeated here so this module never imports mcp.ts (which imports it). */
const ProjectRef = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(64))

/** Search-tool kinds: the record kinds plus `chat` (refused quietly for workers). */
export const SearchToolKind = Schema.Union([SearchKind, Schema.Literal("chat")])

const searchFields = {
  query: KnowledgeQuery,
  kinds: Schema.optionalKey(Schema.Array(SearchToolKind).check(Schema.isMaxLength(9))),
  /** Per group; default 5. */
  limit: Schema.optionalKey(Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 30 }))),
  /**
   * Decision 0146: "what was true then": memory hits are the claims in effect on this date (superseded ones included
   * when they held then), other records those written by then.
   */
  asOf: Schema.optionalKey(DateBound),
}
const readFields = {
  ref: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(300)),
  /** Decision 0150: where a long text file's page starts (the previous answer's `nextOffset`). */
  offset: Schema.optionalKey(Schema.Int.check(Schema.isGreaterThanOrEqualTo(0))),
}
const lookBackFields = {
  query: KnowledgeQuery,
  /** Default: 365 days ago. */
  since: Schema.optionalKey(DateBound),
  /** Default: now. */
  until: Schema.optionalKey(DateBound),
  kinds: Schema.optionalKey(Schema.Array(HistoryKind).check(Schema.isMinLength(1), Schema.isMaxLength(2))),
  /** Default 8. */
  limit: Schema.optionalKey(Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 20 }))),
}

/** Agent tool inputs, spread into `GraphToolInputs` (mcp.ts). */
export const KnowledgeToolInputs = {
  search: Schema.Struct(searchFields),
  read: Schema.Struct(readFields),
  look_back: Schema.Struct(lookBackFields),
} as const
export type KnowledgeToolName = keyof typeof KnowledgeToolInputs

/**
 * The external MCP's versions (decision 0046): the same plus `project` (a project id, slug or name). Not part of
 * `PersonToolInputs` (that table belongs to the onboarding wave); `apps/cloud/src/mcp/knowledge.ts` lists them.
 */
export const KnowledgePersonToolInputs = {
  search: Schema.Struct({ project: ProjectRef, ...searchFields }),
  read: Schema.Struct({ project: ProjectRef, ...readFields }),
  look_back: Schema.Struct({ project: ProjectRef, ...lookBackFields }),
} as const
// #endregion tool-inputs

// #region index-rows
/** A chat message the AgentBody sends to the Project DO's history index (`ProjectPort.indexChat`). */
export const ChatIndexRow = Schema.Struct({
  messageId: Schema.String,
  /** The message's index in the chat. */
  pos: NonNegInt,
  /** Never 0: a backfill uses `created_at`, else a neighbour's date or the backfill time. */
  at: Millis,
  /** The backfill found no `created_at` (look_back still returns it, spec §7.3). */
  dateUnknown: Schema.Boolean,
  role: Schema.Literals(["person", "agent"]),
  /** A person's user id or the agent's id. */
  who: Schema.String,
  via: Schema.NullOr(Schema.String),
  /** "" = remove the message's row. */
  text: Schema.String,
  /** Read at flush time, not at enqueue. Anything but `team` is dropped by the Project DO. */
  visibility: ChatVisibility,
  /** The turn run that wrote an agent message (id `msg_<runId>`); null for people. */
  turnRunId: Schema.NullOr(Schema.String),
  /** The body's knowledge; the Project DO ORs in the turn run's taint. */
  tainted: Schema.Boolean,
})
export type ChatIndexRow = typeof ChatIndexRow.Type

/** A run transcript message the Run DO sends to the Project DO's history index (`ProjectPort.indexTranscript`). */
export const TranscriptIndexRow = Schema.Struct({
  /** frameSeq of the message's first frame (steers: offset, see core `STEER_POS_BASE`). */
  pos: NonNegInt,
  at: Millis,
  role: Schema.Literals(["assistant", "tools", "steer"]),
  text: Schema.String,
})
export type TranscriptIndexRow = typeof TranscriptIndexRow.Type
// #endregion index-rows

// #region decided
/** K0: the light list of every decided card (ref chips, ⌘K), from the Project DO's `decided` projection. */
export const DecidedHeader = Schema.Struct({
  id: ProposalId,
  ref: ShortRef,
  oneLine: Schema.String,
  kind: Schema.Literals(["plan_change", "question", "outward_action"]),
  /** The proposal state (applied, answered, rejected, expired, apply_failed, adopted, …). */
  state: Schema.String,
  /** `resolution.at`. */
  at: Millis,
  subject: Schema.NullOr(EntityRef),
  /** Decision 0144: the decision's ref (D-n). Absent on cards decided before it. */
  decisionRef: Schema.optionalKey(Schema.String),
})
export type DecidedHeader = typeof DecidedHeader.Type
// #endregion decided

// #region usage
export const KnowledgeTool = Schema.Literals(["search", "read", "look_back"])
export const KnowledgeCallerKind = Schema.Literals(["person", "mcp", "lea", "agent", "worker", "validator"])
export type KnowledgeCallerKind = typeof KnowledgeCallerKind.Type

/**
 * One row of the usage log (spec §9.1). `qText` and `refs` are returned only on the requesting person's own
 * person/mcp rows; agent rows never carry text but keep their refs.
 */
export const KnowledgeCall = Schema.Struct({
  at: Millis,
  tool: KnowledgeTool,
  callerKind: KnowledgeCallerKind,
  callerId: Schema.String,
  client: Schema.NullOr(Schema.String),
  runId: Schema.NullOr(Schema.String),
  /** search: kinds or "all"; look_back: kinds + window days ("chat,runs;30d"); read: the ref's kind. */
  scope: Schema.String,
  qLen: NonNegInt,
  qText: Schema.NullOr(Schema.String),
  results: NonNegInt,
  latencyMs: NonNegInt,
  rowsRead: Schema.NullOr(NonNegInt),
  refs: Schema.NullOr(Schema.Array(Schema.String)),
  matched: Schema.NullOr(SearchMatched),
  followed: Schema.Boolean,
})
export type KnowledgeCall = typeof KnowledgeCall.Type

/** `GET /api/workspaces/:ws/projects/:projectId/knowledge/usage` (spec §9.3). */
export const KnowledgeUsage = Schema.Struct({
  projectId: Schema.String,
  since: Millis,
  until: Millis,
  rows: Schema.Array(KnowledgeCall),
  index: Schema.Struct({ records: NonNegInt, history: NonNegInt, dbBytes: NonNegInt }),
})
export type KnowledgeUsage = typeof KnowledgeUsage.Type
// #endregion usage
